<template>
  <div
    class="twitter-post"
    v-loading="loading"
    :element-loading-text="loadingText"
    element-loading-custom-class="csv-loading"
  >
    <campaign-add-widget-dialog
      ref="campaignAddWidgetDialog"
      :campaign="campaign"
      @widget_added="fetchCampaign"
    ></campaign-add-widget-dialog>
    <div class="mb-x2" layout="layout" layout-align="space-between-center">
      <el-button class="el-button-mt" type="default" @click="back"><i class="mdi mdi-arrow-left"></i>戻る</el-button>
      <div v-if="campaign.id" layout-align="end-center">
        <div layout="row">
          <div class="mr-x3">応募者数: {{ formatNumber(campaign.applicantsCount) }}</div>
          <div class="mr-x3">応募総数: {{ formatNumber(entryCount) }}</div>
          <div>{{ lastProcessedData }}: {{ lastProcessedDatetime }}</div>
        </div>
        <div v-if="campaign.type !== 'old_web_instantwin'" style="text-align: end">
          ウィジェット最終更新日時: {{ widgetModified }}
        </div>
      </div>
    </div>
    <el-card class="mb-x2">
      <TwitterPostFilter></TwitterPostFilter>
    </el-card>
    <el-card v-if="error">
      <div class="message message-error">
        データ取得に失敗しました。恐れ入りますが、時間を置いて再度お試しください。
      </div>
    </el-card>
    <el-card v-else-if="isLoaded" v-loading="isLoading">
      <div class="mb-x3" layout="row" layout-align="space-between-center">
        <div>
          <el-button-group>
            <el-button
              :type="viewType === 'list' ? 'primary' : 'default'"
              :disabled="!posts.length"
              @click="changeViewType('list')"
              ><i class="mdi mdi-view-list"></i>一覧</el-button
            >
            <el-button
              :type="viewType === 'grid' ? 'primary' : 'default'"
              :disabled="!posts.length"
              @click="changeViewType('grid')"
              ><i class="mdi mdi-view-module"></i>グリッド</el-button
            >
          </el-button-group>
          <el-select
            class="ml-x1"
            v-if="viewType === 'grid'"
            v-model="sortGrid"
            size="small"
            placeholder="ソート"
            @change="onSorting(sortGrid)"
          >
            <el-option
              v-for="(item, index) of sortGridOptions"
              :key="index"
              :label="item.label"
              :value="item.value"
            ></el-option>
          </el-select>
        </div>
        <div class="header-right" layout="row">
          <el-button
            class="mr-x1"
            v-if="campaign.type !== 'old_web_instantwin'"
            type="default"
            @click="openCampaignAddWiddetDialog"
            ><i class="mdi mdi-view-module"></i>ウィジェット管理</el-button
          >
          <csv-download :download="downloadCsv" :disabled="!posts.length" />
        </div>
      </div>
      <!-- リポスト・引用系は開催前と応募収集URL未設定のあいだ案内文を表示し、収集開始後は応募の有無で切り替える -->
      <template v-if="isRepostEntryCampaign">
        <div class="message message-info mb-x2" v-if="isRepostCampaignBeforeStart">
          このキャンペーンは、{{
            campaignStartDatetime
          }}から開催されます。<br />キャンペーン開始後、応募収集URLを設定してください。設定されるまで応募は収集されません。<br />応募収集URLを設定すると、応募は30分間隔で収集されます。URLの設定時点から7日より前の応募は収集されないため、早めに設定してください。
        </div>
        <div class="message message-info mb-x2" v-else-if="isRepostCampaignBeforeCollect">
          応募収集URLを設定すると、応募の収集を開始します。設定されるまで応募は収集されません。<br />応募は30分間隔で収集されます。URLの設定時点から7日より前の応募は収集されないため、早めに設定してください。
        </div>
        <div class="message message-info" v-else-if="!posts.length">
          応募がないまたはデータが収集されていません。<br />30分間隔で収集されます。
        </div>
      </template>
      <template v-else>
        <div class="message message-info" v-if="!posts.length">
          応募がないまたはデータが収集されていません。<br />ポスト、メンション・返信は30分間隔で収集されます。
        </div>
      </template>
      <div v-if="posts.length">
        <TwitterPostTable
          v-if="isListView"
          :posts="posts"
          :options="options"
          :campaign-type="campaign.type"
          @paging="onPaging"
          @sorting="onSorting"
          @change_is_widget="changeIsWidget"
          @change_is_candidate="changeIsCandidate"
          @change_is_winner="changeIsWinner"
          @change_ng="changeNgAccount"
        ></TwitterPostTable>
        <TwitterPostGrid
          v-if="isGridView"
          :posts="posts"
          :options="options"
          :campaign-type="campaign.type"
          @paging="onPaging"
          @change_is_widget="changeIsWidget"
          @change_is_candidate="changeIsCandidate"
          @change_is_winner="changeIsWinner"
          @change_ng="changeNgAccount"
        ></TwitterPostGrid>
      </div>
    </el-card>
  </div>
</template>

<script lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import moment from 'moment'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'

import CsvDownload from '@/client/components/_atoms/csv_download'
import api from '@/client/core/api'
import util from '@/client/core/util'
import CampaignAddWidgetDialog from '@/client/features/campaigns/components/campaign_add_widget_dialog'
import { isRepostEntryActionType } from '@/client/features/campaigns/utils/campaignUrlSettingTarget'
import {
  RepostCollectionAlertState,
  resolveRepostCollectionAlertState
} from '@/client/features/campaigns/utils/repostCollectionAlert'
import TwitterPostFilter from '@/client/features/twitter_post/twitter_post_filter'
import TwitterPostGrid from '@/client/features/twitter_post/twitter_post_grid'
import TwitterPostTable from '@/client/features/twitter_post/twitter_post_table'
import { formatDatetime, formatNumber } from '@/client/filters/filters'
import { TrackingService } from '@/client/services'
import { useNavbarStore } from '@/client/store/useNavbarStore'
import { useTwitterPostsStore } from '@/client/store/useTwitterPostsStore'
import { isBeforeNttAaaSwitchover } from '@/common/utils'

export default defineComponent({
  components: {
    TwitterPostFilter,
    TwitterPostTable,
    TwitterPostGrid,
    CsvDownload,
    CampaignAddWidgetDialog
  },
  data() {
    return {
      loading: false,
      loadingText: '',
      viewType: 'list',
      sortGrid: '-publishDatetime',
      sortGridOptions: [
        { value: '-publishDatetime', label: '日時(新しい順)' },
        { value: 'publishDatetime', label: '日時(古い順)' }
      ]
    }
  },
  computed: {
    ...mapState(useTwitterPostsStore, {
      posts: state => state.posts,
      params: state => state.params,
      entryCount: state => state.entryCount,
      campaign: state => state.campaign ?? {},
      options: state => ({
        total: state.total,
        pagingNo: state.params.pagingNo,
        limitCount: state.params.limitCount,
        sortBy: state.params.sortBy
      }),
      error: state => state.error,
      firstLoaded: state => state.firstLoaded,
      isLoading: state => state.loading
    }),

    isLoaded() {
      return !this.error && this.firstLoaded
    },

    isListView() {
      return this.viewType === 'list'
    },

    isGridView() {
      return this.viewType === 'grid'
    },

    isRepostEntryCampaign() {
      return (
        this.campaign.snsType === 'twitter' &&
        this.campaign.type === 'keyword' &&
        isRepostEntryActionType(this.campaign.setting?.entryActionType)
      )
    },

    campaignStartDatetime() {
      return moment(this.campaign.startDatetime).format('YYYY年MM月DD日 HH:mm')
    },

    repostCollectionAlertState() {
      return resolveRepostCollectionAlertState({
        type: this.campaign.type,
        entryActionType: this.campaign.setting?.entryActionType,
        status: this.campaign.status,
        hasTweetPostUrl: Boolean(this.campaign.hasTweetPostUrl)
      })
    },

    isRepostCampaignBeforeStart() {
      return this.repostCollectionAlertState === RepostCollectionAlertState.BEFORE_START
    },

    isRepostCampaignBeforeCollect() {
      return this.repostCollectionAlertState === RepostCollectionAlertState.BEFORE_COLLECTION
    },

    lastProcessedData() {
      if (['tweet', 'tweet_media'].includes(this.campaign.setting.entryActionType)) {
        return 'ポスト取得日時'
      }
      return 'データ取得日時'
    },

    lastProcessedDatetime() {
      if (
        isBeforeNttAaaSwitchover() &&
        ['quote_tweet', 'retweet', 'retweet_and_quote_tweet', 'mention'].includes(
          this.campaign.setting.entryActionType
        ) &&
        this.campaign.account.enterpriseToken
      ) {
        return 'リアルタイム反映'
      }

      if (!this.campaign.lastProcessedDatetime) {
        return '-'
      }

      return moment(this.campaign.lastProcessedDatetime).format('YYYY年MM月DD日 HH:mm')
    },

    widgetModified() {
      if (this.campaign.isWidgetCreated === 0) {
        return 'ウィジェット未作成'
      }

      if (!this.campaign.widgetModified) {
        return '-'
      }

      return moment(this.campaign.widgetModified).format('YYYY年MM月DD日 HH:mm')
    }
  },
  async mounted() {
    const params = {
      campaignId: this.$route.params.campaignId
    }

    this.setParams(params)

    await this.fetchCampaign()
    await this.fetchPrizes()

    this.setBreadcrumb([
      {
        name: 'キャンペーン一覧',
        link: '/campaigns'
      },
      {
        name: this.campaign.title,
        link: `/twitter_campaigns/${this.campaign.id}/prizes`,
        iconName: this.campaign.account ? this.campaign.account.name : '削除されたアカウント',
        iconUrl: this.campaign.account ? this.campaign.account.pictureUrl : null,
        iconType: this.campaign.snsType,
        isIcon: true
      },
      {
        name: '応募履歴',
        link: null
      }
    ])
  },
  watch: {
    params() {
      this.fetchPosts()
    }
  },
  methods: {
    formatNumber,

    ...mapActions(useTwitterPostsStore, [
      'setParams',
      'fetchPosts',
      'fetchCampaign',
      'fetchPrizes',
      'reset',
      'updateIsWidget',
      'updateIsCandidate',
      'updateIsWinner',
      'updateNgAccount'
    ]),
    ...mapActions(useNavbarStore, ['setBreadcrumb']),

    /**
     */
    async downloadCsv() {
      TrackingService.sendEvent('click:応募履歴（X）|CSVダウンロード')

      try {
        const posts = await this.getApplicantsForCsv()
        if (!posts.length) {
          return { error: 'ERROR_LOADING' }
        }

        const filename = `campaign_posts_${this.campaign.title}_${moment().format('YYYY_MM_DD')}`
        const csvData = []

        const head = [
          '応募日時',
          '応募グループ',
          'ポスト種別',
          'アカウント名',
          'アカウントスクリーン名',
          'アカウントURL',
          '投稿内容',
          '投稿画像URL',
          '投稿URL',
          'フォロー中',
          'web当選者',
          'NGアカウント',
          '凍結中'
        ]

        if (this.campaign.type !== 'old_web_instantwin') {
          head.push('ウィジェット表示')
        }

        head.push(...['候補者', '当選者'])

        csvData.push(head)

        // CSVデータを格納
        posts.forEach(post => {
          const data = [
            formatDatetime(post.publishDatetime),
            post.prizeName,
            this.getType(post.postType),
            post.name,
            post.screenName,
            'https://www.x.com/' + post.screenName,
            post.message ? post.message.replace(/(\r\n|\n|\r)/gm, '') : '',
            post.mediaUrl,
            `https://www.x.com/${post.screenName}/status/${post.id}`,
            post.isFollowing,
            post.isWebInstantwinWinner,
            post.isNg,
            post.isSuspendedAccount
          ]

          if (this.campaign.type !== 'old_web_instantwin') {
            data.push(post.isWidget)
          }

          data.push(...[post.isCandidate, post.isWinner])

          csvData.push(data)
        })

        return {
          filename,
          data: csvData
        }
      } catch {
        return { error: 'ERROR_LOADING' }
      }
    },

    /**
     * CSV用応募履歴一覧を取得
     */
    async getApplicantsForCsv() {
      this.loading = true
      const { total } = this.options

      this.getTextLoading(0, total)

      const params = Object.assign({}, this.params, {
        pagingNo: 1,
        limitCount: 1000
      })

      let totalResults = []
      let fetchedCount = 0

      // 1000件ごとに取得する
      const maxPages = Math.ceil(total / 1000)
      for (let pageIndex = 0; pageIndex < maxPages && fetchedCount < total; pageIndex++) {
        try {
          const result = await api.get('/twitter_posts', { params })

          if (!result || !result.data || !result.data.posts) {
            throw new Error('ERROR_LOADING')
          }

          const posts = result.data.posts
          if (posts.length === 0) {
            break
          }

          totalResults = totalResults.concat(posts)
          fetchedCount += posts.length

          // 進捗表示を更新
          const displayCount = Math.min(fetchedCount, total)
          this.getTextLoading(displayCount, total)

          params.pagingNo = params.pagingNo + 1

          if (posts.length < 1000) {
            break
          }
        } catch {
          this.loading = false

          return
        }
      }

      this.loading = false
      return totalResults.slice(0, total)
    },

    /**
     * ページを変更イベント
     */
    onPaging(payload: { pagingNo: number; limitCount: number }) {
      this.setParams(payload)
      util.scrollToTop()
    },

    /**
     * ソートを変更イベント
     */
    onSorting(value: string) {
      if (this.params.sortBy === value) {
        return
      }

      this.sortGrid = value

      this.setParams({ sortBy: value })
    },

    /**
     * ポスト種別を取得
     */
    getType(type: string) {
      switch (type) {
        case 'tweet':
          return 'ポスト'
        case 'retweet':
          return '公式リポスト'
        case 'quote_tweet':
          return '引用'
        case 'mention':
          return 'メンション'
        case 'reply':
          return '返信'
        default:
          return ''
      }
    },

    /**
     * 戻る
     */
    back() {
      TrackingService.sendEvent('click:応募履歴（X）|戻る')

      this.$router.push(`/twitter_campaigns/${this.campaign.id}/prizes`)
    },

    /**
     * CSVダウンロードの時、進捗を表示する
     * @params {number} acquiredNumber
     * @params {number} totalNumber
     */
    getTextLoading(acquiredNumber: number, totalNumber: number) {
      this.loadingText = `CSVファイルの作成中です。しばらくお待ちください。\n ${acquiredNumber}件 / ${totalNumber}件`
    },

    /**
     * ビュータイプの変更
     */
    changeViewType(value: string) {
      TrackingService.sendEvent(`click:応募履歴（X）|${value === 'list' ? '一覧' : 'グリッド'}`)

      this.viewType = value
    },

    /**
     * ウィジェットフラグを変更
     */
    async changeIsWidget(payload: { postId: string; isWidget: number }) {
      const result = await this.updateIsWidget(payload)

      if (!result || !result.data) {
        this.showError('ウィジェットの保存に失敗しました')
      }
    },

    /**
     * 候補者フラグを変更
     */
    async changeIsCandidate(payload: { applicantId: number; isCandidate: number }) {
      const result = await this.updateIsCandidate(payload)

      if (!result || !result.data) {
        this.showError('候補者の保存に失敗しました')
      }
    },

    /**
     * 当選者フラグを変更
     */
    async changeIsWinner(payload: { prizeId: number; applicantId: number; isWinner: number }) {
      const result = await this.updateIsWinner(payload)

      if (result.error && result.error.type === 'DM_SENDING_IN_PROGRESS') {
        this.showError('DM送信中のため変更できません。', 'DM送信が終了してから再度お試しください。')
        return
      }

      if (result.error && result.error.type === 'MAX_WINNER_COUNT_OVER') {
        this.showError('残り当選数に空きがありません。')
        return
      }

      if (!result || !result.data) {
        this.showError('当選者の保存に失敗しました')
      }
    },

    showError(title: string, message: string) {
      if (!message) {
        this.$notify({
          title: title,
          customClass: 'danger',
          duration: 5000
        })
      } else {
        this.$notify({
          title: title,
          message: message,
          customClass: 'danger',
          duration: 5000
        })
      }
    },

    openCampaignAddWiddetDialog() {
      TrackingService.sendEvent('click:応募履歴（X）|ウィジェット管理')

      this.$refs.campaignAddWidgetDialog.open()
    },

    /**
     * NGアカウントフラグを変更
     */
    async changeNgAccount(payload: { screenName: string; isNg: boolean; ngApplicantId: number | null }) {
      const result = await this.updateNgAccount(payload)

      if (!result || !result.data) {
        this.$notify({
          title: 'NGアカウントの更新に失敗しました。',
          customClass: 'danger',
          duration: 5000
        })
      }
    }
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    this.reset()
    next()
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.twitter-post :deep(.csv-loading) {
  .el-loading-spinner {
    top: 40vh;

    .el-loading-text {
      white-space: pre-line;
    }
  }
}

.message-center {
  text-align: center;
}

.wait-image {
  max-width: $spacing-unit * 13;
}
</style>
